import { Fragment, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Check, Copy, KeyRound, Pencil, X } from "lucide-react";
import {
  getPlatform,
  getPlatformHealth,
  getPlatformSyncLogs,
  rotateInboundCredentials,
  testConnection,
  updateOutboundCredentials,
  updatePlatformStatus,
} from "@/services/channelCenter";
import type { PlatformCapabilities } from "@/services/types/channelCenter";
import { relativeTime } from "./channelCenterConstants";
import { SyncAttemptsRow, SyncAttemptsToggle } from "./SyncAttemptsRow";
import { PlatformSettingsModal } from "./PlatformSettingsModal";   // PLATEDIT-1
import { PendingSwitchCard } from "./PendingSwitchCard";   // KLOOKPEND-1
import { SettlementSettingsPanel } from "./SettlementSettingsPanel";   // SETTLE-W1
import { CtripProfilePanel } from "./CtripProfilePanel";   // CTRIP-P1
import { WanleCredentialsCard } from "./WanleCredentialsCard";   // WANLE-2
import { WanlePriceCard } from "./WanlePriceCard";   // WANLE-3
import { WanleSandboxCard } from "./WanleSandboxCard";   // WANLE-SBX-1
import { useCan } from "@/auth/permissions";

/** B4 · 「API 凭据」卡 hint——按能力位合成，不再对所有平台写死同一句。 */
function apiCredsHint(cap: PlatformCapabilities): string {
  const authLabel = cap.inbound_auth === "bearer" ? "Bearer token" : cap.inbound_auth === "basic" ? "HTTP Basic" : cap.inbound_auth === "vendor_token" ? "vendorId + token" : null;
  const scopeLabel = cap.outbound ? (authLabel ? "双向各一组" : "仅出站") : "仅入站";
  return authLabel ? `认证方式：${authLabel} · ${scopeLabel}` : `认证方式：${scopeLabel}`;
}

function Pill({ tone, children }: { tone: "green" | "amber" | "gray" | "red"; children: React.ReactNode }) {
  const cls = {
    green: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    gray: "bg-slate-100 text-slate-600",
    red: "bg-rose-50 text-rose-600",
  }[tone];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[12px] font-medium ${cls}`}>
      <span className="h-[7px] w-[7px] rounded-full bg-current" />
      {children}
    </span>
  );
}

function Card({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="rounded-[14px] border border-slate-200 bg-white p-5">
      <h3 className="text-[15px] font-semibold text-slate-900">{title}</h3>
      {hint && <div className="mb-3.5 mt-1 text-[12px] text-slate-400">{hint}</div>}
      <div className={hint ? "" : "mt-3.5"}>{children}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-[10px] border border-slate-200 p-3.5">
      <div className="mb-1 text-[12px] text-slate-400">{label}</div>
      <div className="flex flex-wrap items-center gap-2 text-[13.5px] font-semibold text-slate-900">{children}</div>
    </div>
  );
}

/** label="live"（BOK-2）：重铸 Bokun 正式账号这套独立凭据——用户名也可能是新生成的，所以结果里连用户名一起展示 */
function RotatePasswordModal({ code, onClose, label }: { code: string; onClose: () => void; label?: "live" }) {
  const [result, setResult] = useState<{ username: string; new_password: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const mut = useMutation({
    mutationFn: () => rotateInboundCredentials(code, label),
    onSuccess: setResult,
  });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-xl animate-in zoom-in-95 slide-in-from-bottom-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900"><KeyRound size={18} className="text-blue-600" /> {label === "live" ? "重铸正式账号凭据" : "重铸入站密码"}</h2>
          <button onClick={onClose} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100"><X size={18} /></button>
        </div>
        {result ? (
          <div className="mt-4 flex flex-col gap-3">
            <p className="rounded-lg bg-amber-50 p-3 text-[12.5px] font-medium text-amber-700">
              新密码仅此一次展示，请立即复制保存——离开本弹窗后无法再次查看明文。
            </p>
            {label === "live" && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-[12.5px] text-slate-600" data-testid="rotate-live-username">
                用户名：<code className="font-mono text-[13px] text-slate-900">{result.username}</code>
              </div>
            )}
            <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <code className="flex-1 break-all font-mono text-[13px] text-slate-900" data-testid="rotate-new-password">{result.new_password}</code>
              <button
                type="button"
                onClick={() => { navigator.clipboard.writeText(result.new_password); setCopied(true); }}
                className="flex items-center gap-1 rounded-md bg-blue-600 px-2.5 py-1.5 text-[12px] font-semibold text-white hover:bg-blue-700"
              >
                {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "已复制" : "复制"}
              </button>
            </div>
            <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50">
              完成
            </button>
          </div>
        ) : (
          <div className="mt-4 flex flex-col gap-3">
            <p className="text-[13px] text-slate-500">{label === "live"
              ? "正式账号这套凭据与 Demo 账号那套互不影响。已有则用户名不变、只换密码（旧密码立即失效）；还没有则新生成用户名 + 密码。通过一次性安全链接发给 Bokun，不要走聊天或邮件正文。"
              : "用户名不变，重新生成一个随机密码并落库（bcrypt 哈希存储）。旧密码立即失效。"}</p>
            {mut.isError && <p className="text-[12.5px] text-rose-600">重铸失败，请重试</p>}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50">取消</button>
              <button
                type="button"
                disabled={mut.isPending}
                onClick={() => mut.mutate()}
                className="rounded-lg bg-blue-600 px-4 py-2 text-[13px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {mut.isPending ? "重铸中..." : "确认重铸"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function OutboundCredsModal({ code, onClose }: { code: string; onClose: () => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  // CREDAC-1 加急 · 防浏览器自动填充（10-10 两次把后台登录密码填进了令牌栏）：
  //   ① 两个框一开始是 readOnly——自动填充不会碰 readOnly 的框，用户点进去（focus）才放开；
  //   ② 挂载后立刻、再 400ms 后各清空一次（兜底：万一浏览器还是预填了，用户没动手之前不让它留下）；
  //   ③ 密码下方显示「已输入 N 位」（只显示长度），让用户核对粘贴的是不是完整令牌。
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    const wipe = () => { setUsername((v) => (armed ? v : "")); setPassword((v) => (armed ? v : "")); };
    wipe();
    const t = window.setTimeout(wipe, 400);
    return () => window.clearTimeout(t);
  }, [armed]);
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => updateOutboundCredentials(code, username, password),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["channel-center", "platform", code] });
      onClose();
    },
  });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-xl animate-in zoom-in-95 slide-in-from-bottom-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900"><KeyRound size={18} className="text-blue-600" /> 更新出站凭据</h2>
          <button onClick={onClose} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100"><X size={18} /></button>
        </div>
        <p className="mt-1 text-[12px] text-slate-500">明文只用于加密落库（Fernet），提交后不可读回。</p>
        <div className="mt-4 flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-[12.5px] font-medium text-slate-600">
            用户名
            {/* CREDAC-1 · 录入的是「别人平台的凭据」，不是后台登录——禁止浏览器把保存的登录账号 / 密码自动填进来（10-10 曾把 HUB 令牌顶掉） */}
            <input name="outbound-account" autoComplete="off" readOnly={!armed} onFocus={() => setArmed(true)} value={username} onChange={(e) => setUsername(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-blue-500" />
          </label>
          <label className="flex flex-col gap-1 text-[12.5px] font-medium text-slate-600">
            密码
            <input name="outbound-secret" type="password" autoComplete="new-password" readOnly={!armed} onFocus={() => setArmed(true)} value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-blue-500" />
            <span data-testid="secret-length" className="text-[11.5px] font-normal text-slate-400">已输入 {password.length} 位（只显示长度，不显示内容；粘贴后请核对位数）</span>
          </label>
          {mut.isError && <p className="text-[12.5px] text-rose-600">更新失败，请重试</p>}
          <div className="mt-1 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50">取消</button>
            <button
              type="button"
              disabled={!username || !password || mut.isPending}
              onClick={() => mut.mutate()}
              className="rounded-lg bg-blue-600 px-4 py-2 text-[13px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {mut.isPending ? "保存中..." : "保存"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PlatformDetailPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const rawTab = sp.get("tab");
  const canManageCreds = useCan("api_integration.credentials.manage");   // WANLE-2 · 放在所有提前 return 之前（hooks 顺序）
  const tab: "conn" | "settlement" | "profile" = rawTab === "settlement" ? "settlement" : rawTab === "profile" && code === "ctrip" ? "profile" : "conn";   // SETTLE-W1 · 「结算设置」标签；CTRIP-P1 · 「携程资料」标签（只在 code = ctrip 时出）
  const [showRotate, setShowRotate] = useState(false);
  const [showRotateLive, setShowRotateLive] = useState(false);   // BOK-2 · Bokun 正式账号凭据
  const [showOutboundCreds, setShowOutboundCreds] = useState(false);
  const [settingsMode, setSettingsMode] = useState<"basic" | "outbound" | null>(null);   // PLATEDIT-1
  const [inboundCopied, setInboundCopied] = useState(false);

  const platformQ = useQuery({
    queryKey: ["channel-center", "platform", code],
    queryFn: () => getPlatform(code!),
    enabled: !!code,
  });
  const healthQ = useQuery({
    queryKey: ["channel-center", "health", code],
    queryFn: () => getPlatformHealth(code!),
    enabled: !!code,
  });
  const [openLogId, setOpenLogId] = useState<string | null>(null);   // GYG-LOG-1 · 展开看逐次请求 / 返回
  const logsQ = useQuery({
    queryKey: ["channel-center", "platform-logs", code],
    queryFn: () => getPlatformSyncLogs(code!, 1, 3),
    enabled: !!code,
  });

  const statusMut = useMutation({
    mutationFn: () => updatePlatformStatus(code!, platform!.status === "live" ? "inactive" : "live"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["channel-center", "platform", code] }),
  });
  const testMut = useMutation({
    mutationFn: (direction: "inbound" | "outbound") => testConnection(code!, direction),
  });

  if (!code) return <div className="p-8 text-rose-700">缺平台 code</div>;
  if (platformQ.isLoading) return <div className="p-8 text-slate-500">加载中...</div>;
  if (platformQ.isError || !platformQ.data)
    return (
      <button type="button" onClick={() => platformQ.refetch()} className="m-8 rounded-xl border border-rose-200 bg-rose-50 p-4 text-left text-[13px] font-medium text-rose-700 hover:bg-rose-100">
        加载失败，点击重试
      </button>
    );

  const platform = platformQ.data;
  const cap = platform.capabilities;
  const health = healthQ.data ?? [];
  const logs = logsQ.data ?? [];
  const inboundUrl = cap.inbound_path ? `${window.location.origin}${cap.inbound_path}` : null;

  function handleToggleStatus() {
    const goingLive = platform!.status !== "live";
    const msg = goingLive
      ? "开启后该平台可实时下单，确认开启？"
      : `确认关闭 ${platform!.display_name} 交易闸门？关闭后该平台的新订单将全部被拒（NO_AVAILABILITY）。`;
    if (!window.confirm(msg)) return;
    statusMut.mutate();
  }

  return (
    <div className="flex flex-col gap-4 p-6 lg:p-8">
      <button type="button" onClick={() => navigate("/admin/api-integration")} className="flex w-fit items-center gap-1.5 text-[13px] font-medium text-blue-600 hover:underline">
        <ArrowLeft size={14} /> 返回平台列表
      </button>

      <div className="flex gap-1 border-b border-slate-200" role="tablist" data-testid="platform-tabs">
        {([["conn", "连接与凭据"], ["settlement", "结算设置"], ...(code === "ctrip" ? ([["profile", "携程资料"]] as const) : [])] as const).map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} data-testid={`platform-tab-${k}`}
            onClick={() => setSp(k === "conn" ? {} : { tab: k }, { replace: true })}
            className={`border-b-2 px-3.5 py-2 text-[13.5px] ${tab === k ? "border-blue-600 font-semibold text-blue-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}>{label}</button>
        ))}
      </div>

      {tab === "settlement" && <SettlementSettingsPanel code={code} label={platform.display_name} />}
      {tab === "profile" && <CtripProfilePanel />}

      {tab === "conn" && (<>
      <Card title="平台基础信息">
        <div className="grid grid-cols-2 gap-3">
          <Field label="平台 / 分类">
            {platform.display_name} · OTA 平台
            <button type="button" data-testid="edit-basic-info" onClick={() => setSettingsMode("basic")} className="ml-auto flex items-center gap-1 rounded-md border border-slate-200 px-2.5 py-1 text-[12px] font-medium text-slate-700 hover:bg-slate-50"><Pencil size={12} />编辑基础信息</button>
          </Field>
          {cap.needs_supplier_id && (
            <Field label="Supplier ID">{platform.supplier_id ?? "未配置"}</Field>
          )}
          <Field label="对接阶段">
            <Pill tone={platform.status === "live" ? "green" : "gray"}>{platform.status === "live" ? "测试环境已认证" : "未开通"}</Pill>
            {cap.outbound && (
              <Pill tone="gray">{platform.environment === "sandbox" ? "production 未开通" : "production"}</Pill>
            )}
            {(platform.wanle_sandbox_dates?.length ?? 0) > 0 && <span data-testid="wanle-sandbox-tag"><Pill tone="amber">沙箱测试中</Pill></span>}
          </Field>
          {inboundUrl && (
            <Field label={`我方入站地址（${platform.display_name} 调我们）`}>
              <span className="rounded-md bg-slate-100 px-1.5 py-0.5 font-mono text-[12px] font-normal text-slate-700">{inboundUrl}</span>
              <button
                type="button"
                onClick={() => { navigator.clipboard.writeText(inboundUrl); setInboundCopied(true); }}
                className="ml-auto flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-[12px] font-medium text-slate-700 hover:bg-slate-50"
              >
                {inboundCopied ? <Check size={12} /> : <Copy size={12} />} {inboundCopied ? "已复制" : "复制"}
              </button>
            </Field>
          )}
        </div>
      </Card>

      {code === "ctrip_wanle" && (
        <WanleCredentialsCard supplierId={platform.supplier_id} configured={!!platform.wanle_credentials_configured} urls={platform.wanle_urls ?? null} inboundUrl={cap.inbound_path} environment={platform.environment}
          canEdit={canManageCreds} queryKeys={[["channel-center", "platform", code], ["channel-center", "platforms"]]} />
      )}

      {code === "ctrip_wanle" && (
        <WanlePriceCard settings={platform.wanle_price_settings ?? null} canEdit={canManageCreds} queryKeys={[["channel-center", "platform", code]]} />
      )}
      {code === "ctrip_wanle" && (
        <WanleSandboxCard dates={platform.wanle_sandbox_dates ?? []} canEdit={canManageCreds} queryKeys={[["channel-center", "platform", code], ["channel-center", "platforms"]]} />
      )}

      {platform.pending_capable && <PendingSwitchCard platform={platform} />}   {/* KLOOKPEND-1 · 接口待确认开关（Klook / Civitatis） */}

      <Card title="API 凭据" hint={apiCredsHint(cap)}>
        <div className="grid grid-cols-2 gap-3">
          {code !== "ctrip_wanle" && <Field label={code === "bokun" ? "入站（平台 → 我们）用户名 · Demo 账号" : "入站（平台 → 我们）用户名"}>
            <span className="rounded-md bg-slate-100 px-1.5 py-0.5 font-mono text-[12px] font-normal">{platform.inbound_username ?? "—"}</span>
            <Pill tone={platform.inbound_configured ? "green" : "gray"}>{platform.inbound_configured ? "已配置" : "未配置"}</Pill>
            <button type="button" onClick={() => setShowRotate(true)} className="ml-auto rounded-md border border-slate-200 px-2.5 py-1 text-[12px] font-medium text-slate-700 hover:bg-slate-50">重铸密码</button>
          </Field>}
          {code === "bokun" && (
            <Field label="入站用户名 · 正式账号（独立凭据）">
              {(() => {
                const live = platform.extra_logins?.find((x) => x.label === "live");
                return (
                  <>
                    <span data-testid="live-username" className="rounded-md bg-slate-100 px-1.5 py-0.5 font-mono text-[12px] font-normal">{live?.username ?? "—"}</span>
                    <Pill tone={live ? "green" : "gray"}>{live ? "已配置" : "未生成"}</Pill>
                    <button type="button" data-testid="rotate-live" onClick={() => setShowRotateLive(true)} className="ml-auto rounded-md border border-slate-200 px-2.5 py-1 text-[12px] font-medium text-slate-700 hover:bg-slate-50">{live ? "重铸正式账号凭据" : "生成正式账号凭据"}</button>
                  </>
                );
              })()}
            </Field>
          )}
          {cap.outbound && (
            <Field label="出站（我们 → 平台）用户名">
              <span className="rounded-md bg-slate-100 px-1.5 py-0.5 font-mono text-[12px] font-normal">{platform.outbound_username ?? "—"}</span>
              <Pill tone={platform.outbound_configured ? "green" : "gray"}>{platform.outbound_configured ? "已配置" : "未配置"}</Pill>
              <button type="button" onClick={() => setShowOutboundCreds(true)} className="ml-auto rounded-md border border-slate-200 px-2.5 py-1 text-[12px] font-medium text-slate-700 hover:bg-slate-50">更新凭据</button>
            </Field>
          )}
          {cap.outbound && (
            <Field label="出站根地址">
              <span data-testid="outbound-url" className="break-all rounded-md bg-slate-100 px-1.5 py-0.5 font-mono text-[12px] font-normal">{platform.outbound_base_url || "未配置"}</span>
              <button type="button" data-testid="edit-outbound-url" onClick={() => setSettingsMode("outbound")} className="ml-auto flex items-center gap-1 rounded-md border border-slate-200 px-2.5 py-1 text-[12px] font-medium text-slate-700 hover:bg-slate-50"><Pencil size={12} />修改</button>
            </Field>
          )}
          {cap.outbound && (
            <Field label="出站目标环境">
              <Pill tone={platform.environment === "sandbox" ? "amber" : "green"}>{platform.environment === "sandbox" ? "沙箱 sandbox" : "生产 production"}</Pill>
            </Field>
          )}
          <Field label="我方交易闸门">
            <Pill tone={platform.status === "live" ? "green" : "gray"}>{platform.status === "live" ? "live" : "inactive"}</Pill>
            <button
              type="button"
              disabled={statusMut.isPending}
              onClick={handleToggleStatus}
              className="ml-auto rounded-md border border-slate-200 px-2.5 py-1 text-[12px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {platform.status === "live" ? "关闭" : "开启"}
            </button>
          </Field>
          <Field label="连接测试">
            <button
              type="button"
              disabled={testMut.isPending}
              onClick={() => testMut.mutate("inbound")}
              className="rounded-md bg-blue-600 px-2.5 py-1 text-[12px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              测试入站认证
            </button>
            {cap.outbound && (
              <button
                type="button"
                disabled={testMut.isPending}
                onClick={() => testMut.mutate("outbound")}
                className="rounded-md border border-slate-200 px-2.5 py-1 text-[12px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                测试出站推送
              </button>
            )}
          </Field>
        </div>
        {testMut.data && (
          <div className={`mt-3 rounded-lg p-3 text-[12.5px] ${testMut.data.status === "ok" ? "bg-emerald-50 text-emerald-700" : testMut.data.status === "not_configured" ? "bg-slate-100 text-slate-600" : "bg-rose-50 text-rose-700"}`}>
            [{testMut.data.direction === "inbound" ? "入站" : "出站"}] {testMut.data.message}
            {testMut.data.latency_ms != null && ` · ${testMut.data.latency_ms}ms`}
          </div>
        )}
      </Card>

      <Card title="对接能力状态" hint="已实现的显示真实运行态；未实现的如实标「未开通」，不做演示绿灯">
        <div className="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-3">
          {health.map((c) => (
            <div key={c.key} className={`flex items-center gap-2.5 rounded-[10px] border border-slate-200 p-3.5 ${c.enabled ? "" : "bg-slate-50"}`}>
              <Pill tone={c.enabled ? "green" : "gray"}>{c.enabled ? "" : "—"}</Pill>
              <div>
                <div className={`text-[13px] font-semibold ${c.enabled ? "text-slate-900" : "text-slate-400"}`}>{c.label}</div>
                <div className="text-[11.5px] text-slate-400">{c.detail}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card title="最近同步日志">
        <div className="mb-3 flex justify-end">
          <Link to="/admin/api-integration/sync-logs" className="text-[12px] font-medium text-blue-600 hover:underline">查看全局同步日志 →</Link>
        </div>
        {logs.length === 0 ? (
          <div className="py-6 text-center text-[13px] text-slate-400">暂无同步记录</div>
        ) : (
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-slate-200 text-left text-[12px] font-medium text-slate-400">
                <th className="py-2">时间</th><th>类型</th><th>数量</th><th>状态</th><th>耗时</th><th>请求 / 返回</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <Fragment key={l.id}>
                <tr className="border-b border-slate-100 last:border-0">
                  <td className="py-2.5 tabular-nums">{relativeTime(l.created_at)}</td>
                  <td>{l.event_type}</td>
                  <td className="tabular-nums">{l.items_count ?? "—"}</td>
                  <td><Pill tone={l.status === "sent" ? "green" : l.status === "pending" ? "amber" : "red"}>{l.status}</Pill></td>
                  <td className="tabular-nums">{l.duration_ms != null ? `${(l.duration_ms / 1000).toFixed(1)}s` : "—"}</td>
                  <td><SyncAttemptsToggle open={openLogId === l.id} onClick={() => setOpenLogId(openLogId === l.id ? null : l.id)} /></td>
                </tr>
                {openLogId === l.id && <SyncAttemptsRow eventId={l.id} colSpan={6} />}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      </>)}

      {showRotate && <RotatePasswordModal code={code} onClose={() => { setShowRotate(false); qc.invalidateQueries({ queryKey: ["channel-center", "platform", code] }); }} />}
      {showRotateLive && <RotatePasswordModal code={code} label="live" onClose={() => { setShowRotateLive(false); qc.invalidateQueries({ queryKey: ["channel-center", "platform", code] }); }} />}
      {showOutboundCreds && <OutboundCredsModal code={code} onClose={() => setShowOutboundCreds(false)} />}
      {settingsMode && <PlatformSettingsModal platform={platform} mode={settingsMode} onClose={() => setSettingsMode(null)} />}
    </div>
  );
}
